import React from 'react';
import { Modal } from '@/components/ui/Modal/Modal';
import { useAdminJob, useAdminJobEvents } from '@/hooks/queries';

export function JobDetails({ jobId, onClose }: { jobId: string; onClose: () => void }) {
  const { data: job, isLoading: jobLoading } = useAdminJob(jobId);
  const { data: events = [], isLoading: eventsLoading } = useAdminJobEvents(jobId);
  const loading = jobLoading || eventsLoading;

  return (
    <Modal isOpen={true} onClose={onClose} title={`Job ${jobId}`} size="lg">
      {loading ? (
        <div>Loading…</div>
      ) : !job ? (
        <div>Not found</div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div><span className="text-gray-600">Type:</span> {job.type}</div>
            <div><span className="text-gray-600">Status:</span> {job.status}</div>
            <div><span className="text-gray-600">Queue:</span> {job.queue || 'default'}</div>
            <div><span className="text-gray-600">Org:</span> {job.organization_id || '—'}</div>
            <div><span className="text-gray-600">Attempts:</span> {job.attempt}/{job.max_attempts}</div>
            <div><span className="text-gray-600">Priority:</span> {job.priority ?? 0}</div>
          </div>

          <div>
            <div className="text-sm font-medium text-gray-700 mb-1">Payload</div>
            <pre className="p-3 border rounded bg-gray-50 whitespace-pre-wrap text-xs max-h-64 overflow-auto">{JSON.stringify(sanitize(job.payload), null, 2)}</pre>
          </div>

          <div>
            <div className="text-sm font-medium text-gray-700 mb-1">Events</div>
            <div className="border rounded divide-y max-h-64 overflow-auto">
              {events.length === 0 ? (
                <div className="p-3 text-sm">No events</div>
              ) : events.map((e: any) => (
                <div key={e.id} className="p-3 text-sm flex items-start justify-between gap-4">
                  <div>
                    <div className="font-medium">{e.kind}</div>
                    {e.message && <div className="text-gray-600">{e.message}</div>}
                    {e.data && <pre className="mt-1 bg-gray-50 p-2 rounded text-xs">{JSON.stringify(e.data, null, 2)}</pre>}
                  </div>
                  <div className="text-gray-500">{new Date(e.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

function sanitize(payload: any): any {
  try {
    const obj = typeof payload === 'string' ? JSON.parse(payload) : payload;
    return sanitizeObject(obj);
  } catch {
    return payload;
  }
}

function sanitizeObject(obj: any): any {
  if (obj === null || obj === undefined) return obj;
  
  if (Array.isArray(obj)) {
    return obj.map(sanitizeObject);
  }
  
  if (typeof obj === 'object') {
    const copy: any = {};
    for (const [key, value] of Object.entries(obj)) {
      // Redact sensitive field names
      if (/token|secret|password|key|auth|credential|api_key|private|session/i.test(key)) {
        copy[key] = '***';
      } 
      // Redact sensitive string values (looks like tokens/keys)
      else if (typeof value === 'string' && (
        value.length > 20 && /^[a-zA-Z0-9+/=_-]+$/.test(value) || // Base64-like
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value) || // UUID
        /^sk-[a-zA-Z0-9_-]+$/.test(value) || // OpenAI API key pattern
        /^ya29\.[a-zA-Z0-9_-]+$/.test(value) // Google token pattern
      )) {
        copy[key] = '***';
      }
      // Recursively sanitize nested objects
      else {
        copy[key] = sanitizeObject(value);
      }
    }
    return copy;
  }
  
  return obj;
}


